import History from '@history';
import { createSlice } from '@reduxjs/toolkit';
import { API_V2_ERROR_STATUS, HTTP_FORBIDDEN, HTTP_UNAUTHORIZED } from 'app/constants/http.constant';
import { routesUrl } from 'app/fuse-configs/navigationConfig';
import getCategories from 'app/main/apps/categories/ducks/categories.thunk';
import { getReachableFranchisesApi } from 'app/main/apps/franchises/api';
import { getUserFranchise } from 'app/main/apps/franchises/ducks/franchises.thunk';
import { ReachableFranchiseDTO } from 'app/main/apps/franchises/type';
import { getLoyaltyModel } from 'app/main/apps/loyalties/ducks/loyalty.thunk';
import { getStores, setCurrentStoreIdThunk } from 'app/main/apps/stores/ducks/stores.thunk';
import { getWebPushSubscriptionThunk } from 'app/main/apps/webPush/ducks/webPush.thunk';
import jwtService from 'app/services/jwtService';
import { showMessage } from 'app/store/fuse/messageSlice';
import { replaceNavigation } from 'app/store/fuse/navigationSlice';
import i18n from 'i18n';
import authRoles, { RoleEnum, SpecialRolesEnum } from '../authRoles';
import { getNavigationConfig, getUserRole, shouldPickBrand } from '../utils/utils';
import { UserModelInDTO } from './type';
import { getOwnerPermissionsThunk } from './user.thunk';
import { setUserData } from './userSlice';

const UNAUTHORIZED_LOGIN = 'unauthorized';

type LoginFailure = {
  message?: string;
  isAxiosError?: boolean;
  response?: { status?: number; data?: { error?: { status?: string } } };
};

const asLoginFailure = (error: unknown): LoginFailure => (typeof error === 'object' && error !== null ? error : {});

/** Never shows the server's own message: a failed login must not reveal whether the account exists. */
const getLoginErrorMessage = (error: unknown): string => {
  const { message, isAxiosError, response } = asLoginFailure(error);
  const status = response?.status;
  const errorStatus = response?.data?.error?.status;
  if (status === HTTP_UNAUTHORIZED && errorStatus === API_V2_ERROR_STATUS.INVALID_CREDENTIALS) {
    return i18n.t('pages.login.invalidCredentials');
  }
  if (status === HTTP_FORBIDDEN && errorStatus === API_V2_ERROR_STATUS.DASHBOARD_ACCESS_DENIED) {
    return UNAUTHORIZED_LOGIN;
  }
  if (status || isAxiosError) {
    return i18n.t('pages.marketing.errorConnection');
  }
  return message || i18n.t('pages.marketing.errorConnection');
};

export const submitLogin =
  ({ email, password }: { email: string; password: string }) =>
  async (dispatch: any) => {
    dispatch(loginLoading(true));
    try {
      const user: UserModelInDTO = await jwtService.signInWithEmailAndPassword(email, password);
      if (!user.acl || (!authRoles.admin.includes(user.acl.role) && !authRoles.owner.includes(user.acl.role))) {
        throw new Error(UNAUTHORIZED_LOGIN);
      }
      const userRole = getUserRole(user);
      if (!userRole) {
        History.push(routesUrl.forbidden);
        return;
      }
      const merchantRoles = [RoleEnum.STORE_OWNER, RoleEnum.FRANCHISE_OWNER];
      const hasUserRoleMerchant = merchantRoles.some(role => userRole === role);
      let reachableFranchises: ReachableFranchiseDTO[] = [];
      if (hasUserRoleMerchant) {
        const franchiseId = await dispatch(getUserFranchise());
        if (!franchiseId) {
          dispatch(showMessage({ message: 'An error occurred while connecting to the franchise', variant: 'error' }));
          return;
        }
        const stores = await dispatch(getStores());
        if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
          await dispatch(getWebPushSubscriptionThunk());
        }
        dispatch(setCurrentStoreIdThunk());
        dispatch(getLoyaltyModel());
        // A failure here must not cost the operator their sign-in: no brands read means
        // no switcher, which is the single-brand behaviour.
        reachableFranchises = await getReachableFranchisesApi().catch(() => []);
        dispatch(
          setUserData({
            userData: user,
            role: stores.length === 0 ? SpecialRolesEnum.RESTRICTED_STORE_OWNER : userRole,
            reachableFranchisesCount: reachableFranchises.length,
          }),
        );
        dispatch(getOwnerPermissionsThunk(franchiseId, user.id));
      } else {
        dispatch(setUserData({ userData: user, role: userRole }));
      }
      const navConfig = getNavigationConfig();
      dispatch(replaceNavigation(navConfig));
      dispatch(getCategories());
      dispatch(loginSuccess());

      // Pushed last, once the role is in the store: FuseAuthorization redirects away
      // from /login as soon as the role lands, and this has to win over that.
      if (shouldPickBrand(reachableFranchises.length, jwtService.hasPickedBrand())) {
        History.push(routesUrl.myBrands);
      }
    } catch (error: unknown) {
      const message = getLoginErrorMessage(error);
      dispatch(showMessage({ message, variant: 'error' }));
      dispatch(loginError({ message, status: asLoginFailure(error).response?.status || 500 }));
    } finally {
      dispatch(loginLoading(false));
    }
  };

const initialState = {
  success: false,
  isLoading: false,
  error: {
    username: null,
    password: null,
  },
};

const loginSlice = createSlice({
  name: 'auth/login',
  initialState,
  reducers: {
    loginLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    loginSuccess: state => {
      state.success = true;
    },
    loginError: (state, action) => {
      state.success = false;
      state.error = action.payload;
    },
  },
  extraReducers: {},
});

export const { loginSuccess, loginError, loginLoading } = loginSlice.actions;

export default loginSlice.reducer;
